import useOverlay from "../../hooks/useOverlay";
import { CART_PATH } from "../../routes";
import SelectionDropdown from "./SelectionDropdown";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCart } from "../../context/CartContext";
import { isChoiceAvailable, normalizedCategory, stockMax, usableChoices } from "../../data/catalog";
import { describePieces } from "../../utils/pricing";

const FLASH_MS = 1250;

// Persistent bottom controls of the item viewer: price summary, option select, quantity stepper, add button.
export default function PurchasePanel({ item }) {
  const { open } = useOverlay();
  const { piecesOf, selectionQtyOf, setQuantity, addSelection } = useCart();
  const choices = usableChoices(item);
  const hasChoices = choices.length > 0;
  const selectionLabel = ["Shoes", "Apparel"].includes(normalizedCategory(item)) ? "Select size" : "Select item";
  const inCart = piecesOf(item.id);

  const [qty, setQty] = useState(hasChoices ? 1 : inCart || 1);
  const [selected, setSelected] = useState("");
  const [flash, setFlash] = useState("");
  const [addedThisView, setAddedThisView] = useState(false);
  const flashTimer = useRef(0);
  const stackRef = useRef(null);
  const barRef = useRef(null);
  const selectRef = useRef(null);
  const [mobileHost, setMobileHost] = useState(null);

  useLayoutEffect(() => {
    const media = window.matchMedia?.("(max-width: 720px)");
    if (!media) return undefined;
    const update = () => setMobileHost(media.matches ? document.getElementById("productShade") : null);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // The summary stack sits directly on top of the action bar, whatever height the bar has.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const place = () => {
      if (bar) {
        const height = Math.ceil(bar.getBoundingClientRect().height);
        if (stackRef.current) stackRef.current.style.bottom = `${height}px`;
        bar.closest("#productShade")?.style.setProperty("--product-action-height", `${height}px`);
      }
    };
    const placeSelection = () => {
      bar?.closest("#productShade")?.style.setProperty("--product-selection-height", `${mobileHost && selectRef.current ? Math.ceil(selectRef.current.getBoundingClientRect().height) : 0}px`);
    };
    place();
    placeSelection();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => { place(); placeSelection(); });
    observer.observe(bar);
    if (selectRef.current) observer.observe(selectRef.current);
    return () => observer.disconnect();
  }, [mobileHost]);

  const showFlash = (text) => {
    clearTimeout(flashTimer.current);
    setFlash(text);
    flashTimer.current = setTimeout(() => setFlash(""), FLASH_MS);
  };

  const remaining = item.stockQuantity != null ? Math.max(0, item.stockQuantity - inCart) : Infinity;
  const room = hasChoices ? (selected ? Math.min(remaining, stockMax(item) - selectionQtyOf(item.id, selected)) : 0) : stockMax(item);
  const gated = stockMax(item) === 0 || (hasChoices && (!selected || room === 0));
  const shownQty = Math.min(qty, Math.max(1, room));

  const choose = (value) => {
    setSelected(value);
    setQty(1);
  };

  const submit = () => {
    if (gated || flash) return;
    setAddedThisView(true);
    if (hasChoices) {
      addSelection(item, selected, shownQty);
      setSelected("");
      setQty(1);
      showFlash("Added to Bag");
    } else {
      setQuantity(item, shownQty);
      showFlash(inCart > 0 ? "Updated" : "Added to Bag");
    }
  };

  const label = flash ? `✓ ${flash}` : !hasChoices && inCart > 0 ? "Update Quantity" : "Add to Bag";
  const summaryPieces = hasChoices ? inCart : shownQty;

  const selection = (
    <div className="v150SelectPanel" ref={selectRef}>
            <div className="v150SelectLabel">{selectionLabel}:</div>
            <div className="v150SelectHost">
              <SelectionDropdown label={selectionLabel} value={selected} onChange={choose} options={choices.map(c => {
                const unavailable = !isChoiceAvailable(item, c);
                const maxed = remaining === 0 || selectionQtyOf(item.id, c) >= stockMax(item);
                return { value: c, label: `${c}${unavailable ? " — Out of Stock" : maxed ? " — Max in bag" : ""}`, disabled: unavailable || maxed };
              })} />
            </div>
          </div>
  );

  const actions = (
    <div className="v148PersistentBar" ref={barRef}>
        <div className={`v148ActionRow productActionsInline${addedThisView && inCart > 0 ? " hasCartShortcut" : ""}`}>
          <div className={`v148Stepper${gated ? " v155Disabled" : ""}`}>
            <button type="button" aria-label="Decrease quantity" disabled={gated || shownQty <= 1} onClick={() => setQty(shownQty - 1)}>
              −
            </button>
            <div className="v148QtyValue">{shownQty}</div>
            <button type="button" aria-label="Increase quantity" disabled={gated || shownQty >= room} onClick={() => setQty(shownQty + 1)}>
              +
            </button>
          </div>
          <button
            type="button"
            id="productAdd"
            className={`add${gated && !flash ? " v155Disabled" : ""}${flash ? " added" : ""}`}
            disabled={gated}
            onClick={submit}
          >
            {label}
          </button>
          {addedThisView && inCart > 0 && <button type="button" className="productGoToBag" onClick={() => open(CART_PATH)}>Go to Cart</button>}
        </div>
        <div className="v148Availability">Availability is confirmed after your inquiry is received.</div>
      </div>
  );

  return (
    <>
      <div className="v150UpperStack" ref={stackRef}>
        <div className={`v150CalcPanel${hasChoices ? "" : " v176SingleCalc"}`} aria-live="polite">
          {describePieces(item, summaryPieces)}
        </div>
        {hasChoices && !mobileHost && selection}

      </div>
      {hasChoices && mobileHost && createPortal(selection, mobileHost)}
      {mobileHost ? createPortal(actions, mobileHost) : actions}
    </>
  );
}
